<template>
  <div class="dashboard-container">
    <!-- Progress Bar Loading -->
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="container-fluid max-w-form">
      <!-- Judul Halaman & Tombol Kembali -->
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <div class="page-title mb-1">
            <i class="bi bi-person-plus-fill text-accent"></i>
            {{ isEdit ? "Perbarui Data Atlet" : "Pendaftaran Atlet Baru" }}
          </div>
          <p class="text-secondary small mb-0">
            Silakan lengkapi formulir pendaftaran atlet di bawah ini. Pilih jenis pertandingan (Tanding / Seni) terlebih dahulu.
          </p>
        </div>

        <router-link to="/admin/team-members" class="ui-btn ui-btn--ghost mt-2 mt-sm-0">
          <i class="bi bi-arrow-left"></i> Kembali ke Daftar
        </router-link>
      </div>

      <!-- Formulir Utama -->
      <form @submit.prevent="submitForm" class="member-form">
        <!-- ═══════════════ LANGKAH 1: KATEGORI & ACARA ═══════════════ -->
        <section class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">1</div>
            <div>
              <h2 class="form-card__title">Pilihan Kategori Pertandingan</h2>
              <p class="form-card__subtitle">Pilih jenis pertandingan utama (Tanding atau Seni) beserta nomor pertandingannya.</p>
            </div>
          </div>

          <div class="form-card__body">
            <div class="row g-3 mb-3">
              <!-- Pilih Turnamen / Acara -->
              <div class="col-md-6">
                <label class="ui-field">
                  <span class="ui-label">Acara / Turnamen <span class="text-danger">*</span></span>
                  <select
                    v-model="form.tournament_id"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.tournament_id }"
                    @change="saatAcaraBerubah"
                    :disabled="isEdit"
                  >
                    <option value="" disabled>Pilih Turnamen</option>
                    <option v-for="t in turnamen" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.tournament_id">{{ errors.tournament_id }}</div>
                </label>
              </div>

              <!-- Pilih Kontingen -->
              <div class="col-md-6">
                <div class="ui-field">
                  <label class="ui-label">Kontingen <span class="text-danger">*</span></label>
                  <PilihCari
                    v-model="form.contingent_id"
                    :options="contingents"
                    placeholder="Pilih kontingen"
                    cari-placeholder="Ketik nama kontingen…"
                    :invalid="Boolean(errors.contingent_id)"
                  />
                  <div class="invalid-feedback" v-if="errors.contingent_id">{{ errors.contingent_id }}</div>
                </div>
              </div>
            </div>

            <!-- PILIHAN UTAMA: TANDING VS SENI -->
            <div class="mb-4">
              <label class="ui-label d-block mb-2">Jenis Pertandingan <span class="text-danger">*</span></label>
              <div class="category-toggle-grid">
                <!-- Tombol Tanding -->
                <div
                  class="category-toggle-card"
                  :class="{ 'is-active': jenisUtama === 'TANDING' }"
                  @click="pilihJenisUtama('TANDING')"
                >
                  <div class="category-toggle-card__icon">
                    <i class="bi bi-shield-shaded"></i>
                  </div>
                  <div class="category-toggle-card__text">
                    <strong>TANDING</strong>
                    <small>Pertarungan 1 lawan 1 dengan klasifikasi kelas berat badan</small>
                  </div>
                  <div class="category-toggle-card__check" v-if="jenisUtama === 'TANDING'">
                    <i class="bi bi-check-circle-fill"></i>
                  </div>
                </div>

                <!-- Tombol Seni -->
                <div
                  class="category-toggle-card"
                  :class="{ 'is-active': jenisUtama === 'SENI' }"
                  @click="pilihJenisUtama('SENI')"
                >
                  <div class="category-toggle-card__icon">
                    <i class="bi bi-stars"></i>
                  </div>
                  <div class="category-toggle-card__text">
                    <strong>SENI</strong>
                    <small>Penampilan jurus & keindahan gerak silat perorangan maupun tim</small>
                  </div>
                  <div class="category-toggle-card__check" v-if="jenisUtama === 'SENI'">
                    <i class="bi bi-check-circle-fill"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- SUB-KATEGORI NOMOR SENI (Jika SENI dipilih) -->
            <div v-if="jenisUtama === 'SENI'" class="seni-number-section mb-4">
              <label class="ui-label d-block mb-2">Pilih Nomor Seni <span class="text-danger">*</span></label>
              <div class="seni-pills-grid">
                <!-- Tunggal -->
                <div
                  v-if="isNomorSeniDibuka('Tunggal')"
                  class="seni-pill-card"
                  :class="{
                    'is-active': nomorDipilih === 'Tunggal',
                    'is-full-disabled': kuotaSeni('Tunggal').penuh
                  }"
                  @click="pilihNomorSeni('Tunggal')"
                >
                  <span class="seni-pill-card__badge">1 Atlet</span>
                  <strong>Tunggal</strong>
                  <small>Jurus baku tunggal perorangan</small>
                  <span v-if="kuotaSeni('Tunggal').penuh" class="badge-penuh-seni">
                    <i class="bi bi-x-circle-fill"></i> Penuh (Kuota Habis)
                  </span>
                  <span v-else-if="kuotaSeni('Tunggal').dibatasi" class="badge-sisa-seni">
                    Sisa {{ kuotaSeni('Tunggal').sisa }} slot
                  </span>
                </div>

                <!-- Ganda -->
                <div
                  v-if="isNomorSeniDibuka('Ganda')"
                  class="seni-pill-card"
                  :class="{
                    'is-active': nomorDipilih === 'Ganda',
                    'is-full-disabled': kuotaSeni('Ganda').penuh
                  }"
                  @click="pilihNomorSeni('Ganda')"
                >
                  <span class="seni-pill-card__badge seni-pill-card__badge--team">2 Atlet</span>
                  <strong>Ganda</strong>
                  <small>Kekayaan teknik serang bela berpasangan</small>
                  <span v-if="kuotaSeni('Ganda').penuh" class="badge-penuh-seni">
                    <i class="bi bi-x-circle-fill"></i> Penuh (Kuota Habis)
                  </span>
                  <span v-else-if="kuotaSeni('Ganda').dibatasi" class="badge-sisa-seni">
                    Sisa {{ kuotaSeni('Ganda').sisa }} slot
                  </span>
                </div>

                <!-- Regu -->
                <div
                  v-if="isNomorSeniDibuka('Regu')"
                  class="seni-pill-card"
                  :class="{
                    'is-active': nomorDipilih === 'Regu',
                    'is-full-disabled': kuotaSeni('Regu').penuh
                  }"
                  @click="pilihNomorSeni('Regu')"
                >
                  <span class="seni-pill-card__badge seni-pill-card__badge--team">3 Atlet</span>
                  <strong>Regu</strong>
                  <small>Kekompakan jurus baku 3 pesilat</small>
                  <span v-if="kuotaSeni('Regu').penuh" class="badge-penuh-seni">
                    <i class="bi bi-x-circle-fill"></i> Penuh (Kuota Habis)
                  </span>
                  <span v-else-if="kuotaSeni('Regu').dibatasi" class="badge-sisa-seni">
                    Sisa {{ kuotaSeni('Regu').sisa }} slot
                  </span>
                </div>

                <!-- Solo Kreatif -->
                <div
                  v-if="isNomorSeniDibuka('Solo Kreatif')"
                  class="seni-pill-card"
                  :class="{
                    'is-active': nomorDipilih === 'Solo Kreatif',
                    'is-full-disabled': kuotaSeni('Solo Kreatif').penuh
                  }"
                  @click="pilihNomorSeni('Solo Kreatif')"
                >
                  <span class="seni-pill-card__badge">1 Atlet</span>
                  <strong>Solo Kreatif</strong>
                  <small>Koreografi jurus bebas dengan senjata/musik</small>
                  <span v-if="kuotaSeni('Solo Kreatif').penuh" class="badge-penuh-seni">
                    <i class="bi bi-x-circle-fill"></i> Penuh (Kuota Habis)
                  </span>
                  <span v-else-if="kuotaSeni('Solo Kreatif').dibatasi" class="badge-sisa-seni">
                    Sisa {{ kuotaSeni('Solo Kreatif').sisa }} slot
                  </span>
                </div>
              </div>
            </div>

            <!-- TINGKAT PERTANDINGAN & KELOMPOK USIA -->
            <div class="row g-3">
              <!-- Tingkat Pertandingan (Prestasi / Pemasalan) -->
              <div class="col-md-6" v-if="jenisFiltered.length">
                <label class="ui-field">
                  <span class="ui-label">Tingkat Pertandingan <span class="text-danger">*</span></span>
                  <select
                    v-model="form.jenis_pertandingan_id"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.jenis_pertandingan_id }"
                    @change="saatJenisPertandinganBerubah"
                  >
                    <option :value="null" disabled>Pilih Tingkat</option>
                    <option v-for="j in jenisFiltered" :key="j.id" :value="j.id">{{ labelTingkat(j) }}</option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.jenis_pertandingan_id">{{ errors.jenis_pertandingan_id }}</div>
                </label>
              </div>

              <!-- Kelompok Usia -->
              <div class="col-md-6">
                <label class="ui-field">
                  <span class="ui-label">Kelompok Usia <span class="text-danger">*</span></span>
                  <select
                    v-model="form.age_category_id"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.age_category_id }"
                    @change="fetchCategoryClasses"
                  >
                    <option :value="null" disabled>Pilih Kelompok Usia</option>
                    <option v-for="ac in ageCategories" :key="ac.id" :value="ac.id">{{ ac.name }}</option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.age_category_id">{{ errors.age_category_id }}</div>
                </label>
              </div>
            </div>

            <!-- Ringkasan Kategori Terpilih -->
            <div v-if="form.championship_category_id && kategoriTerpilih" class="mt-3">
              <div class="badge-summary">
                <i class="bi bi-check-circle-fill text-success fs-5"></i>
                <div>
                  Kategori Terdaftar: <strong>{{ kategoriTerpilih.name }}</strong>
                  <span class="text-muted ms-2" v-if="nomorDipilih">({{ nomorDipilih }})</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ LANGKAH 2: DATA ATLET (DINAMIS 1, 2, atau 3 ATLET) ═══════════════ -->
        <section class="mb-4">
          <!-- ATLET 1 (ATLET UTAMA / KETUA TIM) -->
          <div class="form-card mb-4">
            <div class="form-card__header">
              <div class="form-card__badge">2</div>
              <div class="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h2 class="form-card__title">
                    {{ anggotaDibutuhkan > 0 ? "Data Atlet 1 (Ketua Tim)" : "Data Pribadi Atlet" }}
                  </h2>
                  <p class="form-card__subtitle">Lengkapi identitas resmi, fisik, dan pas foto atlet.</p>
                </div>

                <!-- Quick Pick dari Atlet Kontingen yang Sudah Ada -->
                <div v-if="!isEdit && daftarAtletKontingen.length" class="quick-pick-box">
                  <select class="ui-input ui-input--sm" @change="pilihCepatAtletUtama($event)">
                    <option value="">⚡ Pilih dari Atlet yang Sudah Ada...</option>
                    <option v-for="a in daftarAtletKontingen" :key="a.id" :value="a.id">
                      {{ a.name }} (NIK: {{ a.nik || '-' }})
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-card__body">
              <div class="row g-3">
                <!-- Nama Lengkap -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Nama Lengkap Atlet <span class="text-danger">*</span></span>
                    <input
                      type="text"
                      class="ui-input"
                      :class="{ 'is-invalid': errors.name }"
                      v-model="form.name"
                      placeholder="Masukkan nama lengkap sesuai KTP/Akta"
                      required
                    />
                    <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                  </label>
                </div>

                <!-- Jenis Kelamin -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Jenis Kelamin <span class="text-danger">*</span></span>
                    <select
                      class="ui-input"
                      :class="{ 'is-invalid': errors.gender }"
                      v-model="form.gender"
                      @change="fetchCategoryClasses"
                      required
                    >
                      <option value="" disabled>Pilih Jenis Kelamin</option>
                      <option value="male">Putra (Laki-laki)</option>
                      <option value="female">Putri (Perempuan)</option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.gender">{{ errors.gender }}</div>
                  </label>
                </div>

                <!-- NIK (16 Digit) -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <div class="d-flex justify-content-between">
                      <span class="ui-label">Nomor Induk Kependudukan (NIK) <span class="text-danger">*</span></span>
                      <small :class="form.nik.length === 16 ? 'text-success fw-bold' : 'text-muted'">
                        {{ form.nik.length }}/16 Digit
                      </small>
                    </div>
                    <input
                      type="text"
                      maxlength="16"
                      class="ui-input"
                      :class="{ 'is-invalid': errors.nik }"
                      v-model="form.nik"
                      placeholder="16 digit nomor KTP/KIA"
                      required
                    />
                    <div class="invalid-feedback" v-if="errors.nik">{{ errors.nik }}</div>
                  </label>
                </div>

                <!-- No KK (16 Digit) -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <div class="d-flex justify-content-between">
                      <span class="ui-label">Nomor Kartu Keluarga (KK) <span class="text-danger">*</span></span>
                      <small :class="form.family_card_number.length === 16 ? 'text-success fw-bold' : 'text-muted'">
                        {{ form.family_card_number.length }}/16 Digit
                      </small>
                    </div>
                    <input
                      type="text"
                      maxlength="16"
                      class="ui-input"
                      :class="{ 'is-invalid': errors.family_card_number }"
                      v-model="form.family_card_number"
                      placeholder="16 digit nomor Kartu Keluarga"
                      required
                    />
                    <div class="invalid-feedback" v-if="errors.family_card_number">{{ errors.family_card_number }}</div>
                  </label>
                </div>

                <!-- Tempat Lahir -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Tempat Lahir <span class="text-danger">*</span></span>
                    <input
                      type="text"
                      class="ui-input"
                      :class="{ 'is-invalid': errors.birth_place }"
                      v-model="form.birth_place"
                      placeholder="Kota/Kabupaten kelahiran"
                      required
                    />
                    <div class="invalid-feedback" v-if="errors.birth_place">{{ errors.birth_place }}</div>
                  </label>
                </div>

                <!-- Tanggal Lahir (Auto Detect Umur) -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <div class="d-flex justify-content-between">
                      <span class="ui-label">Tanggal Lahir <span class="text-danger">*</span></span>
                      <span v-if="umurDihitung !== null" class="badge bg-primary-subtle text-primary">
                        Umur: {{ umurDihitung }} Tahun
                      </span>
                    </div>
                    <input
                      type="date"
                      class="ui-input"
                      :class="{ 'is-invalid': errors.birth_date }"
                      v-model="form.birth_date"
                      @change="saatTanggalLahirBerubah"
                      required
                    />
                    <div class="invalid-feedback" v-if="errors.birth_date">{{ errors.birth_date }}</div>
                  </label>
                </div>

                <!-- Berat Badan & Tinggi Badan -->
                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Berat Badan (kg) <span class="text-danger" v-if="adalahTanding">*</span></span>
                    <div class="input-unit">
                      <input
                        type="number"
                        step="0.1"
                        min="10"
                        max="250"
                        class="ui-input"
                        :class="{ 'is-invalid': errors.body_weight }"
                        v-model.number="form.body_weight"
                        @input="fetchCategoryClasses"
                        placeholder="Contoh: 52.5"
                      />
                      <span class="unit-badge">kg</span>
                    </div>
                    <div class="invalid-feedback" v-if="errors.body_weight">{{ errors.body_weight }}</div>
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Tinggi Badan (cm)</span>
                    <div class="input-unit">
                      <input
                        type="number"
                        step="1"
                        min="50"
                        max="250"
                        class="ui-input"
                        :class="{ 'is-invalid': errors.body_height }"
                        v-model.number="form.body_height"
                        placeholder="Contoh: 165"
                      />
                      <span class="unit-badge">cm</span>
                    </div>
                    <div class="invalid-feedback" v-if="errors.body_height">{{ errors.body_height }}</div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- ═══════════════ ATLET 2 (Jika GANDA atau REGU) ═══════════════ -->
          <div v-if="anggotaDibutuhkan >= 1 && anggota[0]" class="form-card mb-4">
            <div class="form-card__header form-card__header--team">
              <div class="form-card__badge form-card__badge--team">2</div>
              <div class="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h2 class="form-card__title">Data Atlet 2 (Pasangan / Anggota Tim)</h2>
                  <p class="form-card__subtitle">Identitas resmi untuk pesilat kedua dalam tim.</p>
                </div>

                <button
                  type="button"
                  class="ui-btn ui-btn--sm ui-btn--ghost text-accent"
                  @click="salinAlamatKeAnggota(0)"
                >
                  <i class="bi bi-copy"></i> Samakan Alamat dengan Atlet 1
                </button>
              </div>
            </div>

            <div class="form-card__body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Nama Lengkap Atlet 2 <span class="text-danger">*</span></span>
                    <input type="text" class="ui-input" v-model="anggota[0].name" placeholder="Nama lengkap atlet kedua" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Jenis Kelamin</span>
                    <input
                      type="text"
                      class="ui-input ui-input--disabled"
                      :value="form.gender === 'male' ? 'Putra (Laki-laki)' : (form.gender === 'female' ? 'Putri (Perempuan)' : '-')"
                      disabled
                    />
                    <small class="text-muted">Mengikuti atlet pertama (tim harus satu jenis kelamin).</small>
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">NIK Atlet 2 <span class="text-danger">*</span></span>
                    <input type="text" maxlength="16" class="ui-input" v-model="anggota[0].nik" placeholder="16 digit NIK" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">No. KK Atlet 2 <span class="text-danger">*</span></span>
                    <input type="text" maxlength="16" class="ui-input" v-model="anggota[0].family_card_number" placeholder="16 digit No. KK" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Tempat Lahir <span class="text-danger">*</span></span>
                    <input type="text" class="ui-input" v-model="anggota[0].birth_place" placeholder="Tempat lahir" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Tanggal Lahir <span class="text-danger">*</span></span>
                    <input type="date" class="ui-input" v-model="anggota[0].birth_date" required />
                  </label>
                </div>

                <div class="col-md-3">
                  <label class="ui-field">
                    <span class="ui-label">Berat Badan (kg)</span>
                    <input type="number" step="0.1" class="ui-input" v-model.number="anggota[0].body_weight" placeholder="kg" />
                  </label>
                </div>

                <div class="col-md-3">
                  <label class="ui-field">
                    <span class="ui-label">Tinggi Badan (cm)</span>
                    <input type="number" step="1" class="ui-input" v-model.number="anggota[0].body_height" placeholder="cm" />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Alamat Domisili <span class="text-danger">*</span></span>
                    <input type="text" class="ui-input" v-model="anggota[0].address" placeholder="Alamat atlet 2" required />
                  </label>
                </div>

                <!-- Berkas Dokumen Atlet 2 -->
                <div class="col-12 mt-4 pt-3 border-top" v-if="anggota[0].docs">
                  <h4 class="fs-6 fw-bold text-dark mb-1">
                    <i class="bi bi-folder2-open text-primary me-1"></i> Unggah Pas Foto & Berkas Dokumen Atlet 2
                  </h4>
                  <p class="text-muted small mb-3">Lengkapi pas foto dan dokumen persyaratan untuk pesilat kedua.</p>

                  <div class="row g-3">
                    <!-- 1. Pas Foto Atlet 2 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[0].fotoUrl }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-camera-fill text-primary"></i></div>
                          <div>
                            <h3 class="doc-title">1. Pas Foto Atlet 2</h3>
                            <span class="doc-hint">Rasio 3:4 (JPG, PNG)</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div class="foto-preview-box mb-2">
                            <img v-if="anggota[0].fotoUrl" :src="anggota[0].fotoUrl" alt="Pas Foto 2" class="foto-preview-img" />
                            <div v-else class="foto-placeholder">
                              <i class="bi bi-person-fill"></i>
                              <span>Foto 3x4</span>
                            </div>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[0].fotoUrl ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[0].mengunggahFoto">
                              <input
                                type="file"
                                accept="image/*"
                                class="d-none"
                                :disabled="anggota[0].mengunggahFoto"
                                @change="unggahFotoAnggota(0, $event)"
                              />
                              <span v-if="anggota[0].mengunggahFoto" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[0].fotoUrl ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[0].fotoUrl ? 'Ganti Foto' : 'Pilih Pas Foto' }}</span>
                            </label>
                            <button v-if="anggota[0].fotoUrl" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusFotoAnggota(0)">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 2. KK Atlet 2 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[0].docs.kk.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-file-earmark-person text-success"></i></div>
                          <div>
                            <h3 class="doc-title">2. Kartu Keluarga (KK)</h3>
                            <span class="doc-hint">PDF / Gambar</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[0].docs.kk.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[0].docs.kk.name || 'Kartu-Keluarga.pdf' }}</span>
                              <a :href="anggota[0].docs.kk.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[0].docs.kk.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[0].docs.kk.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[0].docs.kk.uploading"
                                @change="unggahDokumenAnggota(0, 'kk', $event)"
                              />
                              <span v-if="anggota[0].docs.kk.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[0].docs.kk.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[0].docs.kk.path ? 'Ganti Berkas' : 'Unggah KK' }}</span>
                            </label>
                            <button v-if="anggota[0].docs.kk.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(0, 'kk')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 3. KTP / KIA Atlet 2 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[0].docs.ktp.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-card-heading text-info"></i></div>
                          <div>
                            <h3 class="doc-title">3. KTP / KIA / Akta</h3>
                            <span class="doc-hint">PDF / Gambar</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[0].docs.ktp.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[0].docs.ktp.name || 'KTP-KIA.pdf' }}</span>
                              <a :href="anggota[0].docs.ktp.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[0].docs.ktp.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[0].docs.ktp.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[0].docs.ktp.uploading"
                                @change="unggahDokumenAnggota(0, 'ktp', $event)"
                              />
                              <span v-if="anggota[0].docs.ktp.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[0].docs.ktp.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[0].docs.ktp.path ? 'Ganti Berkas' : 'Unggah KTP/KIA' }}</span>
                            </label>
                            <button v-if="anggota[0].docs.ktp.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(0, 'ktp')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 4. Surat Sehat Atlet 2 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[0].docs.surat_sehat.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-heart-pulse-fill text-danger"></i></div>
                          <div>
                            <h3 class="doc-title">4. Surat Sehat</h3>
                            <span class="doc-hint">Dokter / Puskesmas / RS</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[0].docs.surat_sehat.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[0].docs.surat_sehat.name || 'Surat-Sehat.pdf' }}</span>
                              <a :href="anggota[0].docs.surat_sehat.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[0].docs.surat_sehat.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[0].docs.surat_sehat.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[0].docs.surat_sehat.uploading"
                                @change="unggahDokumenAnggota(0, 'surat_sehat', $event)"
                              />
                              <span v-if="anggota[0].docs.surat_sehat.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[0].docs.surat_sehat.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[0].docs.surat_sehat.path ? 'Ganti Berkas' : 'Unggah Surat Sehat' }}</span>
                            </label>
                            <button v-if="anggota[0].docs.surat_sehat.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(0, 'surat_sehat')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 5. Surat Rekomendasi Atlet 2 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[0].docs.surat_rekomendasi.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-award-fill text-warning"></i></div>
                          <div>
                            <h3 class="doc-title">5. Surat Rekomendasi</h3>
                            <span class="doc-hint">Perguruan / Pengcab / Sekolah</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[0].docs.surat_rekomendasi.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[0].docs.surat_rekomendasi.name || 'Surat-Rekomendasi.pdf' }}</span>
                              <a :href="anggota[0].docs.surat_rekomendasi.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[0].docs.surat_rekomendasi.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[0].docs.surat_rekomendasi.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[0].docs.surat_rekomendasi.uploading"
                                @change="unggahDokumenAnggota(0, 'surat_rekomendasi', $event)"
                              />
                              <span v-if="anggota[0].docs.surat_rekomendasi.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[0].docs.surat_rekomendasi.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[0].docs.surat_rekomendasi.path ? 'Ganti Berkas' : 'Unggah Rekomendasi' }}</span>
                            </label>
                            <button v-if="anggota[0].docs.surat_rekomendasi.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(0, 'surat_rekomendasi')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 6. Surat Izin Wali Atlet 2 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[0].docs.surat_izin_wali.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-shield-check text-purple"></i></div>
                          <div>
                            <h3 class="doc-title">6. Surat Izin Wali</h3>
                            <span class="doc-hint">Surat Izin Orang Tua / Wali</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[0].docs.surat_izin_wali.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[0].docs.surat_izin_wali.name || 'Surat-Izin-Wali.pdf' }}</span>
                              <a :href="anggota[0].docs.surat_izin_wali.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[0].docs.surat_izin_wali.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[0].docs.surat_izin_wali.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[0].docs.surat_izin_wali.uploading"
                                @change="unggahDokumenAnggota(0, 'surat_izin_wali', $event)"
                              />
                              <span v-if="anggota[0].docs.surat_izin_wali.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[0].docs.surat_izin_wali.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[0].docs.surat_izin_wali.path ? 'Ganti Berkas' : 'Unggah Izin Wali' }}</span>
                            </label>
                            <button v-if="anggota[0].docs.surat_izin_wali.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(0, 'surat_izin_wali')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ═══════════════ ATLET 3 (Jika REGU) ═══════════════ -->
          <div v-if="anggotaDibutuhkan >= 2 && anggota[1]" class="form-card mb-4">
            <div class="form-card__header form-card__header--team">
              <div class="form-card__badge form-card__badge--team">3</div>
              <div class="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h2 class="form-card__title">Data Atlet 3 (Anggota Tim Regu)</h2>
                  <p class="form-card__subtitle">Identitas resmi untuk pesilat ketiga dalam tim regu.</p>
                </div>

                <button
                  type="button"
                  class="ui-btn ui-btn--sm ui-btn--ghost text-accent"
                  @click="salinAlamatKeAnggota(1)"
                >
                  <i class="bi bi-copy"></i> Samakan Alamat dengan Atlet 1
                </button>
              </div>
            </div>

            <div class="form-card__body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Nama Lengkap Atlet 3 <span class="text-danger">*</span></span>
                    <input type="text" class="ui-input" v-model="anggota[1].name" placeholder="Nama lengkap atlet ketiga" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Jenis Kelamin</span>
                    <input
                      type="text"
                      class="ui-input ui-input--disabled"
                      :value="form.gender === 'male' ? 'Putra (Laki-laki)' : (form.gender === 'female' ? 'Putri (Perempuan)' : '-')"
                      disabled
                    />
                    <small class="text-muted">Mengikuti atlet pertama.</small>
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">NIK Atlet 3 <span class="text-danger">*</span></span>
                    <input type="text" maxlength="16" class="ui-input" v-model="anggota[1].nik" placeholder="16 digit NIK" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">No. KK Atlet 3 <span class="text-danger">*</span></span>
                    <input type="text" maxlength="16" class="ui-input" v-model="anggota[1].family_card_number" placeholder="16 digit No. KK" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Tempat Lahir <span class="text-danger">*</span></span>
                    <input type="text" class="ui-input" v-model="anggota[1].birth_place" placeholder="Tempat lahir" required />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Tanggal Lahir <span class="text-danger">*</span></span>
                    <input type="date" class="ui-input" v-model="anggota[1].birth_date" required />
                  </label>
                </div>

                <div class="col-md-3">
                  <label class="ui-field">
                    <span class="ui-label">Berat Badan (kg)</span>
                    <input type="number" step="0.1" class="ui-input" v-model.number="anggota[1].body_weight" placeholder="kg" />
                  </label>
                </div>

                <div class="col-md-3">
                  <label class="ui-field">
                    <span class="ui-label">Tinggi Badan (cm)</span>
                    <input type="number" step="1" class="ui-input" v-model.number="anggota[1].body_height" placeholder="cm" />
                  </label>
                </div>

                <div class="col-md-6">
                  <label class="ui-field">
                    <span class="ui-label">Alamat Domisili <span class="text-danger">*</span></span>
                    <input type="text" class="ui-input" v-model="anggota[1].address" placeholder="Alamat atlet 3" required />
                  </label>
                </div>

                <!-- Berkas Dokumen Atlet 3 -->
                <div class="col-12 mt-4 pt-3 border-top" v-if="anggota[1].docs">
                  <h4 class="fs-6 fw-bold text-dark mb-1">
                    <i class="bi bi-folder2-open text-primary me-1"></i> Unggah Pas Foto & Berkas Dokumen Atlet 3
                  </h4>
                  <p class="text-muted small mb-3">Lengkapi pas foto dan dokumen persyaratan untuk pesilat ketiga.</p>

                  <div class="row g-3">
                    <!-- 1. Pas Foto Atlet 3 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[1].fotoUrl }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-camera-fill text-primary"></i></div>
                          <div>
                            <h3 class="doc-title">1. Pas Foto Atlet 3</h3>
                            <span class="doc-hint">Rasio 3:4 (JPG, PNG)</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div class="foto-preview-box mb-2">
                            <img v-if="anggota[1].fotoUrl" :src="anggota[1].fotoUrl" alt="Pas Foto 3" class="foto-preview-img" />
                            <div v-else class="foto-placeholder">
                              <i class="bi bi-person-fill"></i>
                              <span>Foto 3x4</span>
                            </div>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[1].fotoUrl ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[1].mengunggahFoto">
                              <input
                                type="file"
                                accept="image/*"
                                class="d-none"
                                :disabled="anggota[1].mengunggahFoto"
                                @change="unggahFotoAnggota(1, $event)"
                              />
                              <span v-if="anggota[1].mengunggahFoto" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[1].fotoUrl ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[1].fotoUrl ? 'Ganti Foto' : 'Pilih Pas Foto' }}</span>
                            </label>
                            <button v-if="anggota[1].fotoUrl" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusFotoAnggota(1)">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 2. KK Atlet 3 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[1].docs.kk.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-file-earmark-person text-success"></i></div>
                          <div>
                            <h3 class="doc-title">2. Kartu Keluarga (KK)</h3>
                            <span class="doc-hint">PDF / Gambar</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[1].docs.kk.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[1].docs.kk.name || 'Kartu-Keluarga.pdf' }}</span>
                              <a :href="anggota[1].docs.kk.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[1].docs.kk.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[1].docs.kk.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[1].docs.kk.uploading"
                                @change="unggahDokumenAnggota(1, 'kk', $event)"
                              />
                              <span v-if="anggota[1].docs.kk.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[1].docs.kk.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[1].docs.kk.path ? 'Ganti Berkas' : 'Unggah KK' }}</span>
                            </label>
                            <button v-if="anggota[1].docs.kk.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(1, 'kk')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 3. KTP / KIA Atlet 3 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[1].docs.ktp.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-card-heading text-info"></i></div>
                          <div>
                            <h3 class="doc-title">3. KTP / KIA / Akta</h3>
                            <span class="doc-hint">PDF / Gambar</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[1].docs.ktp.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[1].docs.ktp.name || 'KTP-KIA.pdf' }}</span>
                              <a :href="anggota[1].docs.ktp.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[1].docs.ktp.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[1].docs.ktp.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[1].docs.ktp.uploading"
                                @change="unggahDokumenAnggota(1, 'ktp', $event)"
                              />
                              <span v-if="anggota[1].docs.ktp.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[1].docs.ktp.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[1].docs.ktp.path ? 'Ganti Berkas' : 'Unggah KTP/KIA' }}</span>
                            </label>
                            <button v-if="anggota[1].docs.ktp.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(1, 'ktp')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 4. Surat Sehat Atlet 3 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[1].docs.surat_sehat.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-heart-pulse-fill text-danger"></i></div>
                          <div>
                            <h3 class="doc-title">4. Surat Sehat</h3>
                            <span class="doc-hint">Dokter / Puskesmas / RS</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[1].docs.surat_sehat.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[1].docs.surat_sehat.name || 'Surat-Sehat.pdf' }}</span>
                              <a :href="anggota[1].docs.surat_sehat.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[1].docs.surat_sehat.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[1].docs.surat_sehat.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[1].docs.surat_sehat.uploading"
                                @change="unggahDokumenAnggota(1, 'surat_sehat', $event)"
                              />
                              <span v-if="anggota[1].docs.surat_sehat.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[1].docs.surat_sehat.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[1].docs.surat_sehat.path ? 'Ganti Berkas' : 'Unggah Surat Sehat' }}</span>
                            </label>
                            <button v-if="anggota[1].docs.surat_sehat.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(1, 'surat_sehat')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 5. Surat Rekomendasi Atlet 3 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[1].docs.surat_rekomendasi.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-award-fill text-warning"></i></div>
                          <div>
                            <h3 class="doc-title">5. Surat Rekomendasi</h3>
                            <span class="doc-hint">Perguruan / Pengcab / Sekolah</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[1].docs.surat_rekomendasi.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[1].docs.surat_rekomendasi.name || 'Surat-Rekomendasi.pdf' }}</span>
                              <a :href="anggota[1].docs.surat_rekomendasi.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[1].docs.surat_rekomendasi.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[1].docs.surat_rekomendasi.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[1].docs.surat_rekomendasi.uploading"
                                @change="unggahDokumenAnggota(1, 'surat_rekomendasi', $event)"
                              />
                              <span v-if="anggota[1].docs.surat_rekomendasi.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[1].docs.surat_rekomendasi.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[1].docs.surat_rekomendasi.path ? 'Ganti Berkas' : 'Unggah Rekomendasi' }}</span>
                            </label>
                            <button v-if="anggota[1].docs.surat_rekomendasi.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(1, 'surat_rekomendasi')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 6. Surat Izin Wali Atlet 3 -->
                    <div class="col-12 col-md-6 col-lg-4">
                      <div class="doc-upload-card" :class="{ 'has-file': anggota[1].docs.surat_izin_wali.path }">
                        <div class="doc-upload-card__header">
                          <div class="doc-icon"><i class="bi bi-shield-check text-purple"></i></div>
                          <div>
                            <h3 class="doc-title">6. Surat Izin Wali</h3>
                            <span class="doc-hint">Surat Izin Orang Tua / Wali</span>
                          </div>
                        </div>
                        <div class="doc-upload-card__body">
                          <div v-if="anggota[1].docs.surat_izin_wali.path" class="doc-file-info mb-2">
                            <i class="bi bi-check-circle-fill text-success fs-5"></i>
                            <div class="text-truncate">
                              <span class="d-block fw-semibold text-truncate small">{{ anggota[1].docs.surat_izin_wali.name || 'Surat-Izin-Wali.pdf' }}</span>
                              <a :href="anggota[1].docs.surat_izin_wali.url" target="_blank" class="small text-primary text-decoration-none">
                                <i class="bi bi-eye"></i> Lihat Berkas
                              </a>
                            </div>
                          </div>
                          <div v-else class="doc-empty-box mb-2">
                            <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                            <span>Belum ada berkas</span>
                          </div>
                          <div class="doc-action">
                            <label class="ui-btn ui-btn--sm" :class="anggota[1].docs.surat_izin_wali.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="anggota[1].docs.surat_izin_wali.uploading">
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                class="d-none"
                                :disabled="anggota[1].docs.surat_izin_wali.uploading"
                                @change="unggahDokumenAnggota(1, 'surat_izin_wali', $event)"
                              />
                              <span v-if="anggota[1].docs.surat_izin_wali.uploading" class="ui-spinner"></span>
                              <i v-else class="bi" :class="anggota[1].docs.surat_izin_wali.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                              <span>{{ anggota[1].docs.surat_izin_wali.path ? 'Ganti Berkas' : 'Unggah Izin Wali' }}</span>
                            </label>
                            <button v-if="anggota[1].docs.surat_izin_wali.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumenAnggota(1, 'surat_izin_wali')">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ LANGKAH 3: KELAS TANDING (KHUSUS NOMOR TANDING) ═══════════════ -->
        <section v-if="adalahTanding" class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">3</div>
            <div>
              <h2 class="form-card__title">Kelas Tanding</h2>
              <p class="form-card__subtitle">Pilih kelas berat badan pertandingan yang sesuai untuk pesilat.</p>
            </div>
          </div>

          <div class="form-card__body">
            <!-- Rekomendasi Kelas Berdasarkan Berat Badan -->
            <div v-if="rekomendasiKelas" class="alert alert-info d-flex align-items-center gap-2 mb-3">
              <i class="bi bi-lightbulb-fill text-warning fs-5"></i>
              <div>
                Rekomendasi Berdasarkan Berat Badan ({{ form.body_weight }} kg):
                <strong>{{ rekomendasiKelas.name }} ({{ rekomendasiKelas.weight_min }} - {{ rekomendasiKelas.weight_max }} kg)</strong>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary ms-2"
                  @click="form.category_class_id = rekomendasiKelas.id"
                >
                  Pilih Kelas Ini
                </button>
              </div>
            </div>

            <label class="ui-field">
              <span class="ui-label">Pilih Kelas Tanding <span class="text-danger">*</span></span>
              <select
                class="ui-input"
                :class="{ 'is-invalid': errors.category_class_id }"
                v-model="form.category_class_id"
                required
              >
                <option :value="null" disabled>Pilih Kelas Tanding</option>
                <option
                  v-for="k in categoryClasses"
                  :key="k.id"
                  :value="k.id"
                  :disabled="kuotaKelas(k.id).penuh"
                  :class="{ 'option-penuh': kuotaKelas(k.id).penuh }"
                >
                  {{ k.name }} ( {{ k.weight_min }} - {{ k.weight_max }} kg ) {{ labelKuota(k.id) }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.category_class_id">{{ errors.category_class_id }}</div>
            </label>

            <small v-if="form.category_class_id && kuotaKelas(form.category_class_id).dibatasi" class="text-muted d-block mt-2">
              <i class="bi bi-ticket-perforated"></i> Sisa kuota peserta kelas ini:
              <strong>{{ kuotaKelas(form.category_class_id).sisa }}</strong> dari {{ kuotaKelas(form.category_class_id).kuota }} peserta.
            </small>

            <small v-if="!categoryClasses.length" class="text-warning d-block mt-2">
              <i class="bi bi-exclamation-triangle"></i> Belum ada kelas yang cocok. Pastikan <strong>Kelompok Usia</strong> dan <strong>Jenis Kelamin</strong> telah dipilih.
            </small>
          </div>
        </section>

        <!-- ═══════════════ LANGKAH 4: DATA WILAYAH & ALAMAT ═══════════════ -->
        <section class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">{{ adalahTanding ? '4' : '3' }}</div>
            <div>
              <h2 class="form-card__title">Data Wilayah & Domisili</h2>
              <p class="form-card__subtitle">Informasi alamat domisili resmi atlet.</p>
            </div>
          </div>

          <div class="form-card__body">
            <div class="row g-3">
              <!-- Negara -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Negara <span class="text-danger">*</span></span>
                  <select v-model="form.country_id" class="ui-input" :class="{ 'is-invalid': errors.country_id }" required>
                    <option :value="null" disabled>Pilih Negara</option>
                    <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.country_name }}</option>
                  </select>
                </label>
              </div>

              <!-- Provinsi -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Provinsi <span class="text-danger">*</span></span>
                  <select v-model="form.province_id" class="ui-input" :class="{ 'is-invalid': errors.province_id }" @change="fetchDistricts(true)" required>
                    <option :value="null" disabled>Pilih Provinsi</option>
                    <option v-for="p in provinces" :key="p.id" :value="p.id">{{ p.name }}</option>
                  </select>
                </label>
              </div>

              <!-- Kabupaten / Kota -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Kabupaten / Kota <span class="text-danger">*</span></span>
                  <select v-model="form.district_id" class="ui-input" :class="{ 'is-invalid': errors.district_id }" @change="fetchSubDistricts(true)" required>
                    <option :value="null" disabled>Pilih Kabupaten/Kota</option>
                    <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                  </select>
                </label>
              </div>

              <!-- Kecamatan -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Kecamatan <span class="text-danger">*</span></span>
                  <select v-model="form.subdistrict_id" class="ui-input" :class="{ 'is-invalid': errors.subdistrict_id }" @change="fetchWards(true)" required>
                    <option :value="null" disabled>Pilih Kecamatan</option>
                    <option v-for="s in sub_districts" :key="s.id" :value="s.id">{{ s.name }}</option>
                  </select>
                </label>
              </div>

              <!-- Kelurahan / Desa -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Kelurahan / Desa <span class="text-danger">*</span></span>
                  <select v-model="form.ward_id" class="ui-input" :class="{ 'is-invalid': errors.ward_id }" required>
                    <option :value="null" disabled>Pilih Kelurahan/Desa</option>
                    <option v-for="w in wards" :key="w.id" :value="w.id">{{ w.name }}</option>
                  </select>
                </label>
              </div>

              <!-- Alamat Lengkap -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Alamat Lengkap (Jalan, RT/RW) <span class="text-danger">*</span></span>
                  <input type="text" class="ui-input" v-model="form.address" placeholder="Jl. Mawar No. 12, RT 01/RW 02" required />
                </label>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ LANGKAH 4: UNGGAH FOTO & BERKAS DOKUMEN ═══════════════ -->
        <section class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">{{ adalahTanding ? '5' : '4' }}</div>
            <div>
              <h2 class="form-card__title">Unggah Pas Foto & Berkas Dokumen Atlet</h2>
              <p class="form-card__subtitle">
                Unggah pas foto resmi dan berkas persyaratan (KK, KTP/KIA, Surat Sehat, Rekomendasi, Izin Wali). Format PDF / JPG / PNG (maks. 10MB per berkas).
              </p>
            </div>
          </div>

          <div class="form-card__body">
            <div class="row g-4">
              <!-- 1. Pas Foto Atlet -->
              <div class="col-12 col-md-6 col-lg-4">
                <div class="doc-upload-card" :class="{ 'has-file': fotoUrl }">
                  <div class="doc-upload-card__header">
                    <div class="doc-icon"><i class="bi bi-camera-fill text-primary"></i></div>
                    <div>
                      <h3 class="doc-title">1. Pas Foto Atlet</h3>
                      <span class="doc-hint">Rasio 3:4 (JPG, PNG)</span>
                    </div>
                  </div>

                  <div class="doc-upload-card__body">
                    <div class="foto-preview-box mb-2">
                      <img v-if="fotoUrl" :src="fotoUrl" alt="Pas Foto" class="foto-preview-img" />
                      <div v-else class="foto-placeholder">
                        <i class="bi bi-person-fill"></i>
                        <span>Foto 3x4</span>
                      </div>
                    </div>

                    <div class="doc-action">
                      <label class="ui-btn ui-btn--sm" :class="fotoUrl ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="mengunggahFoto">
                        <input
                          type="file"
                          accept="image/*"
                          class="d-none"
                          :disabled="mengunggahFoto"
                          @change="unggahFotoLangsung"
                        />
                        <span v-if="mengunggahFoto" class="ui-spinner"></span>
                        <i v-else class="bi" :class="fotoUrl ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                        <span>{{ fotoUrl ? 'Ganti Foto' : 'Pilih Pas Foto' }}</span>
                      </label>
                      <button v-if="fotoUrl" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusFoto">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 2. Kartu Keluarga (KK) -->
              <div class="col-12 col-md-6 col-lg-4">
                <div class="doc-upload-card" :class="{ 'has-file': docs.kk.path }">
                  <div class="doc-upload-card__header">
                    <div class="doc-icon"><i class="bi bi-file-earmark-person text-success"></i></div>
                    <div>
                      <h3 class="doc-title">2. Kartu Keluarga (KK)</h3>
                      <span class="doc-hint">PDF / Gambar</span>
                    </div>
                  </div>

                  <div class="doc-upload-card__body">
                    <div v-if="docs.kk.path" class="doc-file-info mb-2">
                      <i class="bi bi-check-circle-fill text-success fs-5"></i>
                      <div class="text-truncate">
                        <span class="d-block fw-semibold text-truncate small">{{ docs.kk.name || 'Kartu-Keluarga.pdf' }}</span>
                        <a :href="docs.kk.url" target="_blank" class="small text-primary text-decoration-none">
                          <i class="bi bi-eye"></i> Lihat Berkas
                        </a>
                      </div>
                    </div>
                    <div v-else class="doc-empty-box mb-2">
                      <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                      <span>Belum ada berkas</span>
                    </div>

                    <div class="doc-action">
                      <label class="ui-btn ui-btn--sm" :class="docs.kk.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="docs.kk.uploading">
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          class="d-none"
                          :disabled="docs.kk.uploading"
                          @change="unggahDokumen('kk', $event)"
                        />
                        <span v-if="docs.kk.uploading" class="ui-spinner"></span>
                        <i v-else class="bi" :class="docs.kk.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                        <span>{{ docs.kk.path ? 'Ganti Berkas' : 'Unggah KK' }}</span>
                      </label>
                      <button v-if="docs.kk.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumen('kk')">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3. KTP / KIA -->
              <div class="col-12 col-md-6 col-lg-4">
                <div class="doc-upload-card" :class="{ 'has-file': docs.ktp.path }">
                  <div class="doc-upload-card__header">
                    <div class="doc-icon"><i class="bi bi-card-heading text-info"></i></div>
                    <div>
                      <h3 class="doc-title">3. KTP / KIA / Akta</h3>
                      <span class="doc-hint">PDF / Gambar</span>
                    </div>
                  </div>

                  <div class="doc-upload-card__body">
                    <div v-if="docs.ktp.path" class="doc-file-info mb-2">
                      <i class="bi bi-check-circle-fill text-success fs-5"></i>
                      <div class="text-truncate">
                        <span class="d-block fw-semibold text-truncate small">{{ docs.ktp.name || 'KTP-KIA.pdf' }}</span>
                        <a :href="docs.ktp.url" target="_blank" class="small text-primary text-decoration-none">
                          <i class="bi bi-eye"></i> Lihat Berkas
                        </a>
                      </div>
                    </div>
                    <div v-else class="doc-empty-box mb-2">
                      <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                      <span>Belum ada berkas</span>
                    </div>

                    <div class="doc-action">
                      <label class="ui-btn ui-btn--sm" :class="docs.ktp.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="docs.ktp.uploading">
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          class="d-none"
                          :disabled="docs.ktp.uploading"
                          @change="unggahDokumen('ktp', $event)"
                        />
                        <span v-if="docs.ktp.uploading" class="ui-spinner"></span>
                        <i v-else class="bi" :class="docs.ktp.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                        <span>{{ docs.ktp.path ? 'Ganti Berkas' : 'Unggah KTP/KIA' }}</span>
                      </label>
                      <button v-if="docs.ktp.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumen('ktp')">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 4. Surat Keterangan Sehat -->
              <div class="col-12 col-md-6 col-lg-4">
                <div class="doc-upload-card" :class="{ 'has-file': docs.surat_sehat.path }">
                  <div class="doc-upload-card__header">
                    <div class="doc-icon"><i class="bi bi-heart-pulse-fill text-danger"></i></div>
                    <div>
                      <h3 class="doc-title">4. Surat Keterangan Sehat</h3>
                      <span class="doc-hint">Dokter / Puskesmas / RS</span>
                    </div>
                  </div>

                  <div class="doc-upload-card__body">
                    <div v-if="docs.surat_sehat.path" class="doc-file-info mb-2">
                      <i class="bi bi-check-circle-fill text-success fs-5"></i>
                      <div class="text-truncate">
                        <span class="d-block fw-semibold text-truncate small">{{ docs.surat_sehat.name || 'Surat-Sehat.pdf' }}</span>
                        <a :href="docs.surat_sehat.url" target="_blank" class="small text-primary text-decoration-none">
                          <i class="bi bi-eye"></i> Lihat Berkas
                        </a>
                      </div>
                    </div>
                    <div v-else class="doc-empty-box mb-2">
                      <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                      <span>Belum ada berkas</span>
                    </div>

                    <div class="doc-action">
                      <label class="ui-btn ui-btn--sm" :class="docs.surat_sehat.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="docs.surat_sehat.uploading">
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          class="d-none"
                          :disabled="docs.surat_sehat.uploading"
                          @change="unggahDokumen('surat_sehat', $event)"
                        />
                        <span v-if="docs.surat_sehat.uploading" class="ui-spinner"></span>
                        <i v-else class="bi" :class="docs.surat_sehat.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                        <span>{{ docs.surat_sehat.path ? 'Ganti Berkas' : 'Unggah Surat Sehat' }}</span>
                      </label>
                      <button v-if="docs.surat_sehat.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumen('surat_sehat')">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 5. Surat Rekomendasi -->
              <div class="col-12 col-md-6 col-lg-4">
                <div class="doc-upload-card" :class="{ 'has-file': docs.surat_rekomendasi.path }">
                  <div class="doc-upload-card__header">
                    <div class="doc-icon"><i class="bi bi-award-fill text-warning"></i></div>
                    <div>
                      <h3 class="doc-title">5. Surat Rekomendasi</h3>
                      <span class="doc-hint">Perguruan / Pengcab / Sekolah</span>
                    </div>
                  </div>

                  <div class="doc-upload-card__body">
                    <div v-if="docs.surat_rekomendasi.path" class="doc-file-info mb-2">
                      <i class="bi bi-check-circle-fill text-success fs-5"></i>
                      <div class="text-truncate">
                        <span class="d-block fw-semibold text-truncate small">{{ docs.surat_rekomendasi.name || 'Surat-Rekomendasi.pdf' }}</span>
                        <a :href="docs.surat_rekomendasi.url" target="_blank" class="small text-primary text-decoration-none">
                          <i class="bi bi-eye"></i> Lihat Berkas
                        </a>
                      </div>
                    </div>
                    <div v-else class="doc-empty-box mb-2">
                      <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                      <span>Belum ada berkas</span>
                    </div>

                    <div class="doc-action">
                      <label class="ui-btn ui-btn--sm" :class="docs.surat_rekomendasi.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="docs.surat_rekomendasi.uploading">
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          class="d-none"
                          :disabled="docs.surat_rekomendasi.uploading"
                          @change="unggahDokumen('surat_rekomendasi', $event)"
                        />
                        <span v-if="docs.surat_rekomendasi.uploading" class="ui-spinner"></span>
                        <i v-else class="bi" :class="docs.surat_rekomendasi.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                        <span>{{ docs.surat_rekomendasi.path ? 'Ganti Berkas' : 'Unggah Rekomendasi' }}</span>
                      </label>
                      <button v-if="docs.surat_rekomendasi.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumen('surat_rekomendasi')">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 6. Surat Izin Orang Tua / Wali -->
              <div class="col-12 col-md-6 col-lg-4">
                <div class="doc-upload-card" :class="{ 'has-file': docs.surat_izin_wali.path }">
                  <div class="doc-upload-card__header">
                    <div class="doc-icon"><i class="bi bi-shield-check text-purple"></i></div>
                    <div>
                      <h3 class="doc-title">6. Surat Izin Wali</h3>
                      <span class="doc-hint">Surat Izin Orang Tua / Wali</span>
                    </div>
                  </div>

                  <div class="doc-upload-card__body">
                    <div v-if="docs.surat_izin_wali.path" class="doc-file-info mb-2">
                      <i class="bi bi-check-circle-fill text-success fs-5"></i>
                      <div class="text-truncate">
                        <span class="d-block fw-semibold text-truncate small">{{ docs.surat_izin_wali.name || 'Surat-Izin-Wali.pdf' }}</span>
                        <a :href="docs.surat_izin_wali.url" target="_blank" class="small text-primary text-decoration-none">
                          <i class="bi bi-eye"></i> Lihat Berkas
                        </a>
                      </div>
                    </div>
                    <div v-else class="doc-empty-box mb-2">
                      <i class="bi bi-file-earmark-arrow-up text-muted"></i>
                      <span>Belum ada berkas</span>
                    </div>

                    <div class="doc-action">
                      <label class="ui-btn ui-btn--sm" :class="docs.surat_izin_wali.path ? 'ui-btn--ghost' : 'ui-btn--primary'" :disabled="docs.surat_izin_wali.uploading">
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          class="d-none"
                          :disabled="docs.surat_izin_wali.uploading"
                          @change="unggahDokumen('surat_izin_wali', $event)"
                        />
                        <span v-if="docs.surat_izin_wali.uploading" class="ui-spinner"></span>
                        <i v-else class="bi" :class="docs.surat_izin_wali.path ? 'bi-arrow-repeat' : 'bi-upload'"></i>
                        <span>{{ docs.surat_izin_wali.path ? 'Ganti Berkas' : 'Unggah Izin Wali' }}</span>
                      </label>
                      <button v-if="docs.surat_izin_wali.path" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="hapusDokumen('surat_izin_wali')">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ TOMBOL SIMPAN / SUBMIT ═══════════════ -->
        <div class="form-actions mb-5">
          <button type="submit" class="ui-btn ui-btn--primary ui-btn--lg" :disabled="loading">
            <span v-if="loading" class="ui-spinner"></span>
            <i v-else class="bi bi-check-circle-fill"></i>
            <span>{{ isEdit ? "Simpan Perubahan Data Atlet" : "Daftarkan Atlet Sekarang" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { useToast } from "vue-toastification";
import PilihCari from "@/components/ui/PilihCari.vue";

export default {
  name: "MemberForm",

  components: { PilihCari },

  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      memberId: null,
      jenisUtama: 'TANDING', // 'TANDING' | 'SENI'
      nomorDipilih: 'Tanding',
      contingents: [],
      countries: [],
      provinces: [],
      districts: [],
      sub_districts: [],
      wards: [],
      championshipCategories: [],
      ageCategories: [],
      categoryClasses: [],
      turnamen: [],
      daftarAtletKontingen: [],
      keadaanKelas: {},
      fotoUrl: null,
      fotoFilePending: null,
      fotoPath: null,
      mengunggahFoto: false,
      galatFoto: null,
      docs: {
        kk: { path: '', url: '', name: '', uploading: false, error: '' },
        ktp: { path: '', url: '', name: '', uploading: false, error: '' },
        surat_sehat: { path: '', url: '', name: '', uploading: false, error: '' },
        surat_rekomendasi: { path: '', url: '', name: '', uploading: false, error: '' },
        surat_izin_wali: { path: '', url: '', name: '', uploading: false, error: '' },
      },
      kategoriDibuka: [],
      jenisList: [],
      anggota: [],
      form: {
        tournament_id: "",
        jenis_pertandingan_id: null,
        contingent_id: null,
        name: "",
        birth_place: "",
        birth_date: "",
        gender: "male",
        body_weight: "",
        body_height: "",
        blood_type: "",
        nik: "",
        family_card_number: "",
        country_id: null,
        province_id: null,
        district_id: null,
        subdistrict_id: null,
        ward_id: null,
        age_category_id: null,
        category_class_id: null,
        championship_category_id: null,
        match_category_id: null,
        address: "",
        documents: "",
      },
      errors: {},
      loading: false,
      progress: 0,
    };
  },

  computed: {
    adalahTanding() {
      return this.jenisUtama === 'TANDING' || this.nomorDipilih === 'Tanding';
    },

    adalahSeni() {
      return this.jenisUtama === 'SENI';
    },

    anggotaDibutuhkan() {
      if (this.isEdit) return 0;
      const ukuran = { Ganda: 2, Regu: 3 }[this.nomorDipilih] || 1;
      return Math.max(0, ukuran - 1);
    },

    jenisFiltered() {
      if (!this.jenisList.length) return [];
      const champId = Number(this.form.championship_category_id);

      if (this.adalahTanding) {
        return this.jenisList.filter((j) => {
          if (j.championship_category_id) {
            return Number(j.championship_category_id) === champId;
          }
          const nama = (j.name || '').toLowerCase();
          return !['tunggal', 'ganda', 'regu', 'solo kreatif', 'tradisi'].some((n) => nama.includes(n));
        });
      }

      if (this.adalahSeni) {
        const nomor = (this.nomorDipilih || '').toLowerCase();
        // 1. Coba cari opsi spesifik nomor yang dipilih (misal 'Tunggal Prestasi', 'Tunggal Pemasalan')
        const spesifik = this.jenisList.filter((j) => {
          const nama = (j.name || '').toLowerCase();
          return nama.includes(nomor);
        });
        if (spesifik.length) return spesifik;

        // 2. Fallback: opsi seni umum (atau opsi yang memiliki championship_category_id === 2)
        const seniUmum = this.jenisList.filter((j) => {
          if (j.championship_category_id) {
            return Number(j.championship_category_id) === champId;
          }
          return true;
        });
        if (seniUmum.length) return seniUmum;
      }

      return this.jenisList;
    },

    namaJenisTerpilih() {
      if (!this.form.jenis_pertandingan_id) return '';
      const found = this.jenisList.find((j) => Number(j.id) === Number(this.form.jenis_pertandingan_id));
      return found ? (found.name || '') : '';
    },

    kategoriKejuaraanIni() {
      if (!this.form.championship_category_id) return [];
      return this.kategoriDibuka.filter(
        (k) => Number(k.championship_category_id) === Number(this.form.championship_category_id)
      );
    },

    kategoriTerpilih() {
      if (!this.form.championship_category_id || !this.nomorDipilih) return null;

      const targetNomor = (this.nomorDipilih || '').trim().toLowerCase();
      const targetJenis = (this.namaJenisTerpilih || '').trim().toLowerCase();

      // 1. Cari yang cocok nomor dan jenis (Pemasalan / Prestasi)
      const cocokPenuh = this.kategoriKejuaraanIni.find((k) => {
        const kNomor = (k.nomor || '').trim().toLowerCase();
        const kJenis = (k.jenis || '').trim().toLowerCase();
        const kName = (k.name || '').trim().toLowerCase();

        const matchNomor = kNomor === targetNomor || kName.includes(targetNomor);
        const matchJenis = targetJenis
          ? (kJenis === targetJenis || kName.includes(targetJenis) || (kJenis && targetJenis.includes(kJenis)))
          : true;

        return matchNomor && matchJenis;
      });

      if (cocokPenuh) return cocokPenuh;

      // 2. Fallback: jika jenis belum dipilih atau kategori turnamen tidak membedakan jenis
      return this.kategoriKejuaraanIni.find((k) => {
        const kNomor = (k.nomor || '').trim().toLowerCase();
        const kName = (k.name || '').trim().toLowerCase();
        return kNomor === targetNomor || kName.includes(targetNomor);
      }) || null;
    },

    umurDihitung() {
      if (!this.form.birth_date) return null;
      const tglLahir = new Date(this.form.birth_date);
      if (isNaN(tglLahir.getTime())) return null;
      const diff = Date.now() - tglLahir.getTime();
      const ageDate = new Date(diff);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    },

    rekomendasiKelas() {
      if (!this.adalahTanding || !this.form.body_weight || !this.categoryClasses.length) return null;
      const bb = Number(this.form.body_weight);
      return this.categoryClasses.find((k) => bb >= Number(k.weight_min) && bb <= Number(k.weight_max)) || null;
    },
  },

  watch: {
    'form.contingent_id': {
      handler(cid) {
        if (cid) {
          this.muatDaftarAtletKontingen(cid);
        }
      },
    },
    'form.gender': function () {
      this.fetchCategoryClasses();
    },
    'form.age_category_id': function () {
      this.fetchCategoryClasses();
    },
    'form.body_weight': function () {
      if (this.rekomendasiKelas) {
        this.form.category_class_id = this.rekomendasiKelas.id;
      }
    },
    'form.jenis_pertandingan_id': function () {
      this.$nextTick(() => {
        this.hitungMatchCategoryId();
        this.fetchCategoryClasses();
      });
    },
    'form.championship_category_id': function () {
      this.$nextTick(() => {
        this.hitungMatchCategoryId();
      });
    },
    nomorDipilih(baru) {
      if (baru !== 'Tanding') {
        this.form.category_class_id = null;
      }
      this.sesuaikanAnggota();
      this.$nextTick(() => {
        this.hitungMatchCategoryId();
      });
    },
    anggotaDibutuhkan() {
      this.sesuaikanAnggota();
    },
  },

  mounted() {
    this.memberId = this.$route.params.id;
    this.muatAwal();
    if (this.isEdit && this.memberId) {
      this.fetchMemberDetail(this.memberId);
    }
  },

  methods: {
    async muatAwal() {
      try {
        const [resNegara, resProv, resKontingen, resUsia, resKejuaraan, resTurnamen] = await Promise.all([
          http.get('/countries'),
          http.get('/provinces'),
          http.get('/contingents/fetch-all'),
          http.get('/age-categories'),
          http.get('/championship-categories'),
          http.get('/tournaments', { params: { fetch_all: true } }),
        ]);

        this.countries = resNegara.data || [];
        this.provinces = resProv.data || [];
        this.contingents = resKontingen.data || [];
        this.ageCategories = resUsia.data || [];
        this.championshipCategories = resKejuaraan.data || [];
        this.turnamen = resTurnamen.data || [];

        // Set default negara ke Indonesia jika ada
        const indonesia = this.countries.find((c) => c.country_name?.toLowerCase().includes('indonesia'));
        if (indonesia && !this.form.country_id) {
          this.form.country_id = indonesia.id;
        }

        // Baca parameter query dari URL jika diarahkan dari tombol '+ Tambah Atlet'
        const qTournament = this.$route.query.tournament_id;
        const qContingent = this.$route.query.contingent_id;

        if (qTournament) {
          this.form.tournament_id = Number(qTournament);
        } else if (this.turnamen.length && !this.form.tournament_id) {
          this.form.tournament_id = this.turnamen[0].id;
        }

        if (qContingent) {
          this.form.contingent_id = Number(qContingent);
          this.muatDaftarAtletKontingen(this.form.contingent_id);
        }

        if (this.form.tournament_id) {
          await this.saatAcaraBerubah();
        }
      } catch (e) { console.debug("Interceptor handled:", e); }
    },

    isNomorSeniDibuka(nomor) {
      if (!this.kategoriDibuka || !this.kategoriDibuka.length) return true;
      const target = (nomor || '').trim().toLowerCase();
      return this.kategoriDibuka.some((k) => {
        const isSeni = Number(k.championship_category_id) === 2
          || (k.championship_category?.name || '').toLowerCase().includes('seni');
        if (!isSeni) return false;

        const kNomor = (k.nomor || '').trim().toLowerCase();
        const kName = (k.name || '').trim().toLowerCase();
        return kNomor === target || kName.includes(target);
      });
    },

    labelTingkat(j) {
      if (!j || !j.name) return '';
      if (this.adalahSeni && this.nomorDipilih) {
        // Jika nama jenis pertandingan diawali nomor seni terpilih (misal "Tunggal Prestasi"),
        // sederhanakan teks opsi menjadi "Prestasi" agar tidak dobel/ambigu
        const reg = new RegExp(`^${this.nomorDipilih}\\s+`, 'i');
        if (reg.test(j.name)) {
          return j.name.replace(reg, '');
        }
      }
      return j.name;
    },

    pilihJenisUtama(jenis) {
      this.jenisUtama = jenis;
      if (jenis === 'TANDING') {
        this.nomorDipilih = 'Tanding';
        const tandingCat = this.championshipCategories.find((c) => c.name?.toLowerCase().includes('tanding'));
        if (tandingCat) this.form.championship_category_id = tandingCat.id;
      } else {
        const seniCat = this.championshipCategories.find((c) => c.name?.toLowerCase().includes('seni'));
        if (seniCat) this.form.championship_category_id = seniCat.id;
        const calonNomor = ['Tunggal', 'Ganda', 'Regu', 'Solo Kreatif'].filter((n) => this.isNomorSeniDibuka(n));
        if (!this.nomorDipilih || !this.isNomorSeniDibuka(this.nomorDipilih) || this.kuotaSeni(this.nomorDipilih).penuh) {
          const tersedia = calonNomor.find((n) => !this.kuotaSeni(n).penuh);
          this.nomorDipilih = tersedia || (calonNomor[0] || 'Tunggal');
        }
      }
      this.$nextTick(() => {
        this.sesuaikanPilihanJenis();
        this.hitungMatchCategoryId();
      });
    },

    pilihNomorSeni(nomor) {
      const kSeni = this.kuotaSeni(nomor);
      if (kSeni.penuh) {
        this.toast.warning(`Nomor Seni ${nomor} sudah penuh (kuota habis) untuk kategori usia dan jenis kelamin yang dipilih.`);
        return;
      }
      this.nomorDipilih = nomor;
      this.$nextTick(() => {
        this.sesuaikanPilihanJenis();
        this.hitungMatchCategoryId();
      });
    },

    sesuaikanPilihanJenis() {
      if (this.jenisFiltered.length) {
        const ada = this.jenisFiltered.some((j) => Number(j.id) === Number(this.form.jenis_pertandingan_id));
        if (!ada) {
          this.form.jenis_pertandingan_id = this.jenisFiltered[0].id;
        }
      } else {
        this.form.jenis_pertandingan_id = null;
      }
    },

    async saatAcaraBerubah() {
      if (!this.form.tournament_id) return;
      try {
        const [resKategori, resJenis, resKuota] = await Promise.all([
          http.get(`/fetch-match-categories`, { params: { tournament_id: this.form.tournament_id } }),
          http.get(`/jenis-pertandingan`, { params: { tournament_id: this.form.tournament_id } }),
          http.get(`/billings/kuota-kelas`, { params: { tournament_id: this.form.tournament_id } }),
        ]);

        this.kategoriDibuka = resKategori.data || [];
        this.jenisList = resJenis.data || [];
        this.keadaanKelas = resKuota.data?.kelas || resKuota.data || [];

        this.pilihJenisUtama(this.jenisUtama);
      } catch (e) { console.debug("Interceptor handled:", e); }
    },

    saatJenisPertandinganBerubah() {
      this.$nextTick(() => {
        this.hitungMatchCategoryId();
        this.fetchCategoryClasses();
      });
    },

    hitungMatchCategoryId() {
      const match = this.kategoriTerpilih;
      this.form.match_category_id = match ? match.id : null;
    },

    saatTanggalLahirBerubah() {
      const umur = this.umurDihitung;
      if (umur === null || !this.ageCategories.length) return;

      const usiaCocok = this.ageCategories.find((ac) => {
        const min = Number(ac.min_age || 0);
        const max = Number(ac.max_age || 99);
        return umur >= min && umur <= max;
      });

      if (usiaCocok) {
        this.form.age_category_id = usiaCocok.id;
        this.fetchCategoryClasses();
        this.toast.info(`Kelompok usia disesuaikan otomatis ke: ${usiaCocok.name} (${umur} thn)`);
      }
    },

    async fetchCategoryClasses() {
      if (!this.form.age_category_id) {
        this.categoryClasses = [];
        return;
      }

      try {
        const { data } = await http.get(`/category-classes/fetch-class/${this.form.age_category_id}`, {
          params: {
            tournament_id: this.form.tournament_id,
            gender: this.form.gender,
            age_category_id: this.form.age_category_id,
          },
        });
        this.categoryClasses = data || [];

        // Auto-select rekomendasi jika ada
        if (this.rekomendasiKelas && !this.form.category_class_id) {
          this.form.category_class_id = this.rekomendasiKelas.id;
        }
      } catch (e) {
        this.categoryClasses = [];
      }
    },

    async muatDaftarAtletKontingen(contingentId) {
      try {
        const { data } = await http.get('/team-members', {
          params: { contingent_id: contingentId, fetch_all: true },
        });
        this.daftarAtletKontingen = Array.isArray(data) ? data : data?.data ?? [];
      } catch (e) {
        this.daftarAtletKontingen = [];
      }
    },

    pilihCepatAtletUtama(e) {
      const id = e.target.value;
      if (!id) return;
      const a = this.daftarAtletKontingen.find((item) => String(item.id) === String(id));
      if (!a) return;

      this.form.name = a.name || '';
      this.form.nik = a.nik || '';
      this.form.family_card_number = a.family_card_number || '';
      this.form.birth_place = a.birth_place || '';
      this.form.birth_date = a.birth_date || '';
      this.form.gender = a.gender || 'male';
      this.form.body_weight = a.body_weight || '';
      this.form.body_height = a.body_height || '';
      this.form.address = a.address || this.form.address;
      if (a.photo_url) this.fotoUrl = a.photo_url;
      if (a.photo) this.fotoPath = a.photo;

      if (a.documents) {
        try {
          const parsed = typeof a.documents === 'string' ? JSON.parse(a.documents) : a.documents;
          if (typeof parsed === 'object' && parsed !== null) {
            for (const [k, path] of Object.entries(parsed)) {
              if (this.docs[k]) {
                this.docs[k].path = path;
                this.docs[k].url = a.document_urls?.[k] || (path.startsWith('http') ? path : `/storage/${path}`);
                this.docs[k].name = path.split('/').pop();
              }
            }
          }
        } catch (err) {
          console.debug("Docs parse error:", err);
        }
      }

      this.saatTanggalLahirBerubah();
      this.toast.success(`Data ${a.name} berhasil diisi otomatis.`);
      e.target.value = '';
    },

    salinAlamatKeAnggota(index) {
      if (!this.anggota[index]) return;
      this.anggota[index].address = this.form.address;
      this.toast.info(`Alamat berhasil disalin ke Atlet ${index + 2}.`);
    },

    sesuaikanAnggota() {
      const butuh = this.anggotaDibutuhkan;
      while (this.anggota.length < butuh) {
        this.anggota.push({
          name: '',
          birth_place: '',
          birth_date: '',
          gender: this.form.gender || 'male',
          nik: '',
          family_card_number: '',
          address: this.form.address || '',
          body_weight: '',
          body_height: '',
          photo: '',
          fotoUrl: null,
          mengunggahFoto: false,
          documents: '',
          docs: {
            kk: { path: '', url: '', name: '', uploading: false, error: '' },
            ktp: { path: '', url: '', name: '', uploading: false, error: '' },
            surat_sehat: { path: '', url: '', name: '', uploading: false, error: '' },
            surat_rekomendasi: { path: '', url: '', name: '', uploading: false, error: '' },
            surat_izin_wali: { path: '', url: '', name: '', uploading: false, error: '' },
          },
        });
      }
      if (this.anggota.length > butuh) {
        this.anggota = this.anggota.slice(0, butuh);
      }
    },

    kuotaKelas(id) {
      if (!id) return { dibatasi: false, penuh: false, sisa: 0, kuota: 0 };
      const list = Array.isArray(this.keadaanKelas) ? this.keadaanKelas : Object.values(this.keadaanKelas || {});
      const k = list.find((item) => String(item.id) === String(id) && item.jenis === 'tanding')
        || (typeof this.keadaanKelas === 'object' && this.keadaanKelas[id]);
      return k || { dibatasi: false, penuh: false, sisa: 0, kuota: 0 };
    },

    kuotaSeni(nomor) {
      if (!nomor || !this.form.tournament_id || !this.form.age_category_id) {
        return { dibatasi: false, penuh: false, sisa: 0, kuota: 0 };
      }

      const list = Array.isArray(this.keadaanKelas) ? this.keadaanKelas : Object.values(this.keadaanKelas || {});
      const item = list.find((k) => {
        if (k.jenis !== 'seni') return false;
        const matchAge = String(k.age_category_id) === String(this.form.age_category_id);
        const matchGender = !k.gender || !this.form.gender || k.gender === this.form.gender;
        const matchNomor = k.nomor?.toLowerCase() === nomor.toLowerCase() || k.name?.toLowerCase().includes(nomor.toLowerCase());
        return matchAge && matchGender && matchNomor;
      });

      return item || { dibatasi: false, penuh: false, sisa: 0, kuota: 0 };
    },

    labelKuota(id) {
      const k = this.kuotaKelas(id);
      if (!k.dibatasi) return '';
      if (k.penuh) return ' — [PENUH / Kuota Habis]';
      return ` — [Sisa ${k.sisa} slot]`;
    },

    async unggahFotoLangsung(e) {
      const berkas = e.target.files?.[0];
      if (!berkas) return;

      this.mengunggahFoto = true;
      this.galatFoto = null;
      const fd = new FormData();
      fd.append('file', berkas);
      fd.append('type', 'photo');

      try {
        const { data } = await http.post('/atlet/unggah-dokumen', fd);
        this.fotoUrl = data.file_url;
        this.fotoPath = data.file_path;
        this.form.photo = data.file_path;
        this.toast.success('Pas foto berhasil diunggah.');
      } catch (err) {
        this.galatFoto = 'Foto gagal diunggah.';
        this.toast.error('Foto gagal diunggah. Pastikan format JPG/PNG.');
      } finally {
        this.mengunggahFoto = false;
      }
    },

    hapusFoto() {
      this.fotoUrl = null;
      this.fotoPath = null;
      this.form.photo = '';
    },

    async unggahFotoAnggota(index, e) {
      const berkas = e.target.files?.[0];
      if (!berkas || !this.anggota[index]) return;

      this.anggota[index].mengunggahFoto = true;
      const fd = new FormData();
      fd.append('file', berkas);
      fd.append('type', 'photo');

      try {
        const { data } = await http.post('/atlet/unggah-dokumen', fd);
        this.anggota[index].fotoUrl = data.file_url;
        this.anggota[index].photo = data.file_path;
        this.toast.success(`Pas foto Atlet ${index + 2} berhasil diunggah.`);
      } catch (err) {
        this.toast.error(`Foto Atlet ${index + 2} gagal diunggah. Pastikan format JPG/PNG.`);
      } finally {
        this.anggota[index].mengunggahFoto = false;
      }
    },

    hapusFotoAnggota(index) {
      if (!this.anggota[index]) return;
      this.anggota[index].fotoUrl = null;
      this.anggota[index].photo = '';
    },

    async unggahDokumen(jenis, event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.docs[jenis].uploading = true;
      this.docs[jenis].error = '';
      this.docs[jenis].name = file.name;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', jenis);

      try {
        const { data } = await http.post('/atlet/unggah-dokumen', fd);
        this.docs[jenis].path = data.file_path;
        this.docs[jenis].url = data.file_url;
        this.docs[jenis].uploading = false;
        this.sinkronkanDokumen();
        this.toast.success(`Berkas ${this.judulDokumen(jenis)} berhasil diunggah.`);
      } catch (err) {
        this.docs[jenis].uploading = false;
        this.docs[jenis].error = 'Gagal mengunggah berkas.';
        this.toast.error(`Gagal mengunggah ${this.judulDokumen(jenis)}.`);
      }
    },

    hapusDokumen(jenis) {
      this.docs[jenis].path = '';
      this.docs[jenis].url = '';
      this.docs[jenis].name = '';
      this.sinkronkanDokumen();
    },

    sinkronkanDokumen() {
      const obj = {};
      for (const [k, v] of Object.entries(this.docs)) {
        if (v.path) obj[k] = v.path;
      }
      this.form.documents = Object.keys(obj).length ? JSON.stringify(obj) : '';
    },

    async unggahDokumenAnggota(index, jenis, event) {
      const file = event.target.files?.[0];
      if (!file || !this.anggota[index]) return;

      const targetDoc = this.anggota[index].docs[jenis];
      targetDoc.uploading = true;
      targetDoc.error = '';
      targetDoc.name = file.name;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', jenis);

      try {
        const { data } = await http.post('/atlet/unggah-dokumen', fd);
        targetDoc.path = data.file_path;
        targetDoc.url = data.file_url;
        targetDoc.uploading = false;
        this.sinkronkanDokumenAnggota(index);
        this.toast.success(`Berkas ${this.judulDokumen(jenis)} Atlet ${index + 2} berhasil diunggah.`);
      } catch (err) {
        targetDoc.uploading = false;
        targetDoc.error = 'Gagal mengunggah berkas.';
        this.toast.error(`Gagal mengunggah berkas Atlet ${index + 2}.`);
      }
    },

    hapusDokumenAnggota(index, jenis) {
      if (!this.anggota[index]) return;
      const targetDoc = this.anggota[index].docs[jenis];
      targetDoc.path = '';
      targetDoc.url = '';
      targetDoc.name = '';
      this.sinkronkanDokumenAnggota(index);
    },

    sinkronkanDokumenAnggota(index) {
      if (!this.anggota[index]) return;
      const obj = {};
      for (const [k, v] of Object.entries(this.anggota[index].docs)) {
        if (v.path) obj[k] = v.path;
      }
      this.anggota[index].documents = Object.keys(obj).length ? JSON.stringify(obj) : '';
    },

    judulDokumen(jenis) {
      const map = {
        kk: 'Kartu Keluarga (KK)',
        ktp: 'KTP / KIA / Akta',
        surat_sehat: 'Surat Keterangan Sehat',
        surat_rekomendasi: 'Surat Rekomendasi',
        surat_izin_wali: 'Surat Izin Orang Tua / Wali',
      };
      return map[jenis] || jenis;
    },

    async fetchDistricts(resetChild = true) {
      if (resetChild) {
        this.form.district_id = null;
        this.form.subdistrict_id = null;
        this.form.ward_id = null;
        this.districts = [];
        this.sub_districts = [];
        this.wards = [];
      }
      if (!this.form.province_id) return;
      try {
        const { data } = await http.get('/districts', { params: { province_id: this.form.province_id } });
        this.districts = data || [];
      } catch (e) { console.debug("Fetch error:", e); }
    },

    async fetchSubDistricts(resetChild = true) {
      if (resetChild) {
        this.form.subdistrict_id = null;
        this.form.ward_id = null;
        this.sub_districts = [];
        this.wards = [];
      }
      if (!this.form.district_id) return;
      try {
        const { data } = await http.get('/subdistricts', { params: { district_id: this.form.district_id } });
        this.sub_districts = data || [];
      } catch (e) { console.debug("Fetch error:", e); }
    },

    async fetchWards(resetChild = true) {
      if (resetChild) {
        this.form.ward_id = null;
        this.wards = [];
      }
      if (!this.form.subdistrict_id) return;
      try {
        const { data } = await http.get('/wards', { params: { subdistrict_id: this.form.subdistrict_id } });
        this.wards = data || [];
      } catch (e) { console.debug("Fetch error:", e); }
    },

    async fetchMemberDetail(id) {
      this.loading = true;
      try {
        const { data } = await http.get(`/team-members/${id}`);
        this.form = { ...this.form, ...data };
        if (data.photo_url) this.fotoUrl = data.photo_url;
        if (data.photo) this.fotoPath = data.photo;

        if (data.documents) {
          try {
            const parsed = typeof data.documents === 'string' ? JSON.parse(data.documents) : data.documents;
            if (typeof parsed === 'object' && parsed !== null) {
              for (const [k, path] of Object.entries(parsed)) {
                if (this.docs[k]) {
                  this.docs[k].path = path;
                  this.docs[k].url = data.document_urls?.[k] || (path.startsWith('http') ? path : `/storage/${path}`);
                  this.docs[k].name = path.split('/').pop();
                }
              }
            }
          } catch (e) {
            console.debug("Docs parse error:", e);
          }
        }

        if (data.match_category) {
          const mc = data.match_category;
          if (mc.championship_category_id) {
            this.form.championship_category_id = mc.championship_category_id;
          }
          if (mc.nomor) {
            this.nomorDipilih = mc.nomor;
            this.jenisUtama = mc.nomor.toLowerCase() === 'tanding' ? 'TANDING' : 'SENI';
          } else if (mc.name?.toLowerCase().includes('tanding')) {
            this.nomorDipilih = 'Tanding';
            this.jenisUtama = 'TANDING';
          } else {
            this.jenisUtama = 'SENI';
          }

          if (data.jenis_pertandingan_id) {
            this.form.jenis_pertandingan_id = data.jenis_pertandingan_id;
          } else if (mc.jenis && this.jenisList.length) {
            const foundJ = this.jenisList.find((j) => j.name?.toLowerCase() === mc.jenis?.toLowerCase());
            if (foundJ) this.form.jenis_pertandingan_id = foundJ.id;
          }
        }

        if (this.form.province_id) await this.fetchDistricts(false);
        if (this.form.district_id) await this.fetchSubDistricts(false);
        if (this.form.subdistrict_id) await this.fetchWards(false);
      } catch (e) {
        this.toast.error('Gagal memuat detail atlet.');
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      this.errors = {};

      this.hitungMatchCategoryId();
      this.sinkronkanDokumen();

      if (!this.form.name || !this.form.nik || !this.form.contingent_id) {
        this.toast.error('Mohon lengkapi semua kolom wajib.');
        return;
      }

      if (this.adalahTanding && !this.form.category_class_id) {
        this.toast.error('Kelas tanding wajib dipilih untuk nomor Tanding.');
        return;
      }

      if (!this.form.match_category_id && this.kategoriTerpilih) {
        this.form.match_category_id = this.kategoriTerpilih.id;
      }

      this.loading = true;
      try {
        const endpoint = this.isEdit ? `/team-members/${this.memberId}` : '/team-members';
        const method = this.isEdit ? 'put' : 'post';

        const payload = {
          ...this.form,
          match_category_id: this.form.match_category_id || (this.kategoriTerpilih ? this.kategoriTerpilih.id : undefined),
          photo: this.fotoPath || undefined,
          anggota: this.anggotaDibutuhkan > 0
            ? this.anggota.map((a, i) => {
                this.sinkronkanDokumenAnggota(i);
                return {
                  name: a.name,
                  birth_place: a.birth_place,
                  birth_date: a.birth_date,
                  gender: this.form.gender,
                  nik: a.nik,
                  family_card_number: a.family_card_number,
                  address: a.address,
                  body_weight: a.body_weight || null,
                  body_height: a.body_height || null,
                  photo: a.photo || null,
                  documents: a.documents || null,
                };
              })
            : undefined,
        };

        await http[method](endpoint, payload);

        this.toast.success(this.isEdit ? 'Data atlet berhasil diperbarui!' : 'Atlet berhasil didaftarkan!');
        this.$router.push('/admin/team-members');
      } catch (err) {
        if (err.response?.data?.errors) {
          this.errors = err.response.data.errors;
          const msg = Object.values(this.errors).flat().join(' ');
          this.toast.error(msg || 'Terjadi kesalahan pada data formulir.');
        } else {
          this.toast.error(err.response?.data?.message || 'Gagal menyimpan data atlet.');
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.max-w-form {
  max-width: 1040px;
  margin-inline: auto;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.form-card {
  background: var(--bg-surface, #ffffff);
  border: 1px solid var(--border-subtle, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.05));
  overflow: hidden;
  transition: border-color 0.2s ease;
}

.form-card:hover {
  border-color: var(--border-medium, #d1d5db);
}

.form-card__header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-sunken, #f9fafb);
  border-bottom: 1px solid var(--border-subtle, #e5e7eb);
}

.form-card__header--team {
  background: rgba(232, 72, 8, 0.04);
  border-bottom-color: rgba(232, 72, 8, 0.15);
}

.form-card__badge {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--accent, #e84808);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 800;
}

.form-card__badge--team {
  background: var(--text-secondary);
}

.form-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
}

.form-card__subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: var(--text-secondary, #6b7280);
}

.form-card__body {
  padding: var(--space-5);
}

/* Category Toggle Grid (Tanding vs Seni) */
.category-toggle-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

@media (max-width: 640px) {
  .category-toggle-grid {
    grid-template-columns: 1fr;
  }
}

.category-toggle-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 1rem 1.25rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg, 12px);
  cursor: pointer;
  user-select: none;
  transition: all var(--transition-fast);
}

.category-toggle-card:hover {
  border-color: var(--border-strong);
  background: var(--bg-hover);
}

.category-toggle-card.is-active {
  border: 2px solid var(--accent);
  background: var(--accent-subtle);
  box-shadow: var(--shadow-sm);
}

.category-toggle-card__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  font-size: 1.35rem;
  color: var(--text-secondary);
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md, 10px);
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.category-toggle-card.is-active .category-toggle-card__icon {
  color: var(--accent);
  background: var(--bg-surface);
  border-color: rgba(240, 80, 8, 0.25);
}

.category-toggle-card__text strong {
  display: block;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
}

.category-toggle-card__text small {
  display: block;
  font-size: 0.78rem;
  color: var(--text-secondary);
  line-height: 1.35;
}

.category-toggle-card__check {
  position: absolute;
  top: 10px;
  right: 12px;
  color: var(--accent);
  font-size: 1.15rem;
}

/* Seni Number Pills Grid */
.seni-pills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-3);
}

.seni-pill-card {
  position: relative;
  padding: var(--space-3) var(--space-4);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  user-select: none;
  transition: all var(--transition-fast);
}

.seni-pill-card:hover:not(.is-full-disabled) {
  border-color: var(--accent);
  background: var(--bg-hover);
}

.seni-pill-card.is-active {
  border: 2px solid var(--accent);
  background: var(--accent-subtle);
  box-shadow: var(--shadow-sm);
}

.seni-pill-card__badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.68rem;
  font-weight: 700;
  border-radius: var(--radius-sm, 6px);
  background: var(--bg-sunken);
  color: var(--text-secondary);
  margin-bottom: 0.35rem;
  border: 1px solid var(--border-subtle);
}

.seni-pill-card__badge--team {
  background: var(--accent-subtle);
  color: var(--accent);
  border-color: rgba(240, 80, 8, 0.2);
}

.seni-pill-card strong {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary);
}

.seni-pill-card small {
  display: block;
  font-size: 0.74rem;
  color: var(--text-secondary);
  line-height: 1.3;
}

/* ── Disabled & Full State Styles ── */
.seni-pill-card.is-full-disabled {
  background: var(--bg-sunken) !important;
  border: 1px dashed var(--border-subtle) !important;
  cursor: not-allowed !important;
  opacity: 0.6;
  box-shadow: none !important;
}

.seni-pill-card.is-full-disabled strong {
  color: var(--text-muted) !important;
}

.seni-pill-card.is-full-disabled small {
  color: var(--text-muted) !important;
}

.seni-pill-card.is-full-disabled .seni-pill-card__badge {
  background: var(--bg-surface) !important;
  color: var(--text-muted) !important;
  border-color: var(--border-subtle) !important;
}

.badge-penuh-seni {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--danger-600);
}

.badge-sisa-seni {
  display: block;
  margin-top: 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--success-600);
}

/* Option styling in select */
option:disabled,
.option-penuh {
  color: var(--text-muted) !important;
  background-color: var(--bg-sunken) !important;
  font-style: italic;
}

.ui-input:disabled,
.ui-input--disabled {
  background-color: var(--bg-sunken) !important;
  color: var(--text-muted) !important;
  border-color: var(--border-subtle) !important;
  cursor: not-allowed;
}

/* Input Units */
.input-unit {
  position: relative;
  display: flex;
  align-items: center;
}

.input-unit .ui-input {
  padding-right: 42px;
}

.unit-badge {
  position: absolute;
  right: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-muted, #9ca3af);
  pointer-events: none;
}

/* ── Modern Document Upload Card ── */
.doc-upload-card {
  background: var(--bg-surface);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg, 12px);
  padding: 1.15rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  transition: all var(--transition-fast);
}

.doc-upload-card:hover {
  border-color: var(--accent);
  background: var(--bg-hover);
}

.doc-upload-card.has-file {
  border-style: solid;
  border-color: rgba(22, 163, 74, 0.4);
  background: rgba(22, 163, 74, 0.05);
}

.doc-upload-card__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.doc-icon {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
}

.doc-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.doc-hint {
  font-size: 0.74rem;
  color: var(--text-secondary);
  display: block;
}

.foto-preview-box {
  width: 84px;
  height: 110px;
  margin-inline: auto;
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  border: 1px solid var(--border-subtle);
  background: var(--bg-sunken);
  display: flex;
  align-items: center;
  justify-content: center;
}

.foto-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.foto-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 1.8rem;
  color: var(--text-muted);
}

.foto-placeholder span {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.doc-file-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-sunken);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid rgba(22, 163, 74, 0.35);
  color: var(--text-primary);
}

.doc-empty-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.85rem 0.5rem;
  background: var(--bg-sunken);
  border-radius: var(--radius-md);
  border: 1px dashed var(--border-subtle);
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.doc-action {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.5rem;
}

.text-purple {
  color: #a855f7 !important;
}

/* Badge Summary */
.badge-summary {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.2);
  border-radius: var(--radius-md, 8px);
  font-size: 0.9rem;
  color: #15803d;
}

.form-actions {
  display: flex;
  justify-content: center;
  padding-top: var(--space-3);
}

.progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: var(--accent, #e84808);
  z-index: 9999;
}
</style>